import React, { useState } from 'react';
import { Button, RadioGroup, ScaleRadioGroup } from '../UI';
import { kuesionerData } from '../../helpers/kuesioner';
import { calculateAgeMonths } from '../../helpers/utils';
import { getAssessmentRecommendations } from '../../helpers/assessmentRecommendations';

// --- Form for CHAT with special scoring ---
export function ChatForm({ child, onSave }) {
    const ageInMonths = calculateAgeMonths(child.dob.toDate());
    const relevantQuestions = kuesionerData.chat.filter(q => ageInMonths >= q.age);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);
    const handleAnswer = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: value }));
    };
    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.keys(answers).length < relevantQuestions.length) {
            alert("Harap jawab semua pertanyaan.");
            return;
        }
        setLoading(true);

        // CHAT Scoring Logic (Corrected and Verified)
        let score = 0;
        relevantQuestions.forEach(q => {
            const rawAnswer = answers[q.id];
            const isYesAnswer = (typeof rawAnswer === 'string' && rawAnswer.toLowerCase() === 'ya'); // True if user answered 'Ya'

            let isRiskForThisQuestion = false;
            if (q.reverseScore === true) {
                // If reverseScore is true, 'Tidak' is a risk
                if (!isYesAnswer) { // If user answered 'Tidak'
                    isRiskForThisQuestion = true;
                }
            } else {
                // If reverseScore is false, 'Ya' is a risk
                if (isYesAnswer) { // If user answered 'Ya'
                    isRiskForThisQuestion = true;
                }
            }

            if (isRiskForThisQuestion) {
                score++;
            }
        });

        // Panggil fungsi rekomendasi dengan skor dan umur anak
        const recommendations = getAssessmentRecommendations('chat', score, relevantQuestions.length, ageInMonths);
        const data = {
            chat: {
                status: recommendations.status,
                interpretasi: recommendations.interpretation,
                rekomendasi: recommendations.recommendations,
                tindakLanjut: recommendations.followUp,
                tingkatUrgency: recommendations.urgency,
                detail: `Skor Risiko: ${score}/${relevantQuestions.length}`
            }
        };
        onSave(data, recommendations.status);
    };

    const yesNoOptions = [
        { value: 'Ya', label: 'Ya', emoji: '✅' },
        { value: 'Tidak', label: 'Tidak', emoji: '❌' }
    ];

    if (relevantQuestions.length === 0) {
        return (
            <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl">
                <span className="text-4xl mb-4 block">🧩</span>
                <p className="text-gray-600 font-medium">Skrining CHAT tidak relevan untuk usia anak saat ini.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-orange-100 to-yellow-100 p-6 rounded-2xl border border-orange-200">
                <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">🧩</span>
                    <h3 className="font-bold text-orange-800">Skrining CHAT</h3>
                </div>
                <p className="text-orange-700 text-sm opacity-80">Checklist for Autism in Toddlers</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {relevantQuestions.map((item, index) => (
                    <div key={item.id} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                        <div className="mb-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-full text-sm font-bold mr-3">
                                {index + 1}
                            </span>
                            <label className="text-gray-800 font-medium text-base leading-relaxed">{item.q}</label>
                        </div>
                        <RadioGroup
                            name={`chat-${item.id}`}
                            options={yesNoOptions}
                            value={answers[item.id] || ''}
                            onChange={(value) => handleAnswer(item.id, value)}
                        />
                    </div>
                ))}
                <Button type="submit" disabled={loading} className="mt-8">
                    {loading ? '🔄 Menyimpan...' : '💾 Simpan Hasil CHAT'}
                </Button>
            </form>
        </div>
    );
}

// --- Form for GPPH with 0-3 scale scoring ---
export function GpphForm({ child, onSave }) {
    const ageInMonths = calculateAgeMonths(child.dob.toDate());
    const relevantQuestions = kuesionerData.gpph.filter(q => ageInMonths >= q.age);
    const [answers, setAnswers] = useState({});
    const [loading, setLoading] = useState(false);

    const handleAnswer = (id, value) => {
        setAnswers(prev => ({ ...prev, [id]: parseInt(value) }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (Object.keys(answers).length < relevantQuestions.length) {
            alert("Harap jawab semua pertanyaan.");
            return;
        }
        setLoading(true);
        
        const score = Object.values(answers).reduce((sum, val) => sum + val, 0);
        const recommendations = getAssessmentRecommendations('gpph', score, relevantQuestions.length, ageInMonths);
        
        const data = { 
            gpph: { 
                status: recommendations.status, 
                interpretasi: recommendations.interpretation,
                rekomendasi: recommendations.recommendations,
                tindakLanjut: recommendations.followUp,
                tingkatUrgency: recommendations.urgency,
                detail: `Skor Total: ${score}/${relevantQuestions.length * 3}` 
            } 
        };
        onSave(data, recommendations.status);
    };

    if (relevantQuestions.length === 0) {
        return (
            <div className="text-center p-8 bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl">
                <span className="text-4xl mb-4 block">⚡</span>
                <p className="text-gray-600 font-medium">Skrining GPPH tidak relevan untuk usia anak saat ini.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-red-100 to-pink-100 p-6 rounded-2xl border border-red-200">
                <div className="flex items-center space-x-3 mb-2">
                    <span className="text-2xl">⚡</span>
                    <h3 className="font-bold text-red-800">Skrining GPPH</h3>
                </div>
                <p className="text-red-700 text-sm opacity-80">Gangguan Pemusatan Perhatian dan Hiperaktivitas</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {relevantQuestions.map((item, index) => (
                    <div key={item.id} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
                        <div className="mb-4">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-full text-sm font-bold mr-3">
                                {index + 1}
                            </span>
                            <label className="text-gray-800 font-medium text-base leading-relaxed">{item.q}</label>
                        </div>
                        <ScaleRadioGroup
                            name={`gpph-${item.id}`}
                            value={answers[item.id]?.toString() || ''}
                            onChange={(value) => handleAnswer(item.id, value)}
                        />
                    </div>
                ))}
                <Button type="submit" disabled={loading} className="mt-8">
                    {loading ? '🔄 Menyimpan...' : '💾 Simpan Hasil GPPH'}
                </Button>
            </form>
        </div>
    );
}
